const getFirstDefinedValue = (source, keys) => {
    for (const key of keys) {
        if (source?.[key] !== undefined && source?.[key] !== null) {
            return source[key];
        }
    }

    return null;
};

export const EFILE_STATUS_OPTIONS = [
    "Needs Extension",
    "On Extension",
    "Accepted",
    "Rejected",
];

const EFILE_STATUS_ENUM_NAMES = [
    "NEEDS_EXTENSION",
    "ON_EXTENSION",
    "ACCEPTED",
    "REJECTED",
];

const getEfileStatusIndex = (value) => {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value === "object") {
        return getEfileStatusIndex(
            value.id ?? value.value ?? value.name ?? value.label,
        );
    }

    if (typeof value === "number") {
        return Number.isInteger(value) && value >= 0 && value < EFILE_STATUS_OPTIONS.length
            ? value
            : null;
    }

    const raw = String(value).trim();
    if (/^\d+$/.test(raw)) {
        const numericValue = Number(raw);
        return numericValue >= 0 && numericValue < EFILE_STATUS_OPTIONS.length
            ? numericValue
            : null;
    }

    const normalized = raw.replace(/[\s-]+/g, "_").toUpperCase();
    const compact = normalized.replace(/_/g, "");
    const enumIndex = EFILE_STATUS_ENUM_NAMES.findIndex(
        (name) => name === normalized || name.replace(/_/g, "") === compact,
    );
    return enumIndex >= 0 ? enumIndex : null;
};

export const normalizeEfileStatus = (value) => {
    const index = getEfileStatusIndex(value);
    return index === null ? null : EFILE_STATUS_OPTIONS[index];
};

export const serializeEfileStatus = (value, representationSource) => {
    const index = getEfileStatusIndex(value);
    if (index === null) return null;

    if (typeof representationSource === "number") return index;
    if (typeof representationSource === "string") {
        const source = representationSource.trim();
        if (/^\d+$/.test(source)) return String(index);
        if (/^[A-Z0-9_-]+$/.test(source)) return EFILE_STATUS_ENUM_NAMES[index];
        return EFILE_STATUS_OPTIONS[index];
    }

    // The backend enum uses its default System.Text.Json numeric representation.
    return index;
};

export const isEfileStatusApplicable = (clientService) => {
    const value = getFirstDefinedValue(clientService, [
        "isEfileStatusActive",
        "IsEfileStatusActive",
    ]);

    if (value === true || value === 1) return true;

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        return normalized === "true" || normalized === "1";
    }

    return false;
};

export const getClientServiceEfileStatus = (clientService) =>
    getFirstDefinedValue(clientService, [
        "eFileStatus",
        "EFileStatus",
        "efileStatus",
        "EfileStatus",
    ]);

export const getEfileStatusDisplayLabel = (clientService) => {
    if (!isEfileStatusApplicable(clientService)) {
        return "E-file status is not applicable";
    }

    const status = getClientServiceEfileStatus(clientService);
    if (status === null || String(status).trim() === "") {
        return "E-file status has not been set";
    }

    return `E-file status: ${status}`;
};

export const isAcceptedEfileStatus = (value) => {
    return normalizeEfileStatus(value) === "Accepted";
};

export const shouldPromptForEfileCompletion = (clientService) =>
    clientService?.isEfileStatusActive === true &&
    !isAcceptedEfileStatus(clientService?.efileStatus);
